import React, { useState, useEffect, useCallback } from "react";
import { Search, ChevronLeft, ChevronRight, Calendar, Tag, Newspaper } from "lucide-react";
import PageHero from "@/components/PageHero";
import FloatingEditButton from "@/components/FloatingEditButton";
import SEO from "@/components/SEO";
import Reveal from "@/components/motion/Reveal";
import Section from "@/components/ui/Section";
import Card, { CardBody } from "@/components/ui/Card";
import CTAButton from "@/components/ui/CTAButton";
import FilterPills from "@/components/ui/FilterPills";
import SmartImage from "@/components/ui/SmartImage";
import EmptyState from "@/components/ui/EmptyState";
import { CardGridSkeleton } from "@/components/ui/Skeleton";
import { apiGet } from "@/lib/api";

const CATEGORIES = [
  { value: "all", label: "Todas" },
  { value: "delegacion", label: "Noticias de la Delegación" },
  { value: "diocesis", label: "Diócesis de Huelva" },
  { value: "iglesia-espana", label: "Iglesia en España" },
  { value: "blog", label: "Blog" },
  { value: "recursos", label: "Recursos" },
];

const CAT_LABELS = Object.fromEntries(CATEGORIES.map(c => [c.value, c.label]));

function ArticleCard({ article }) {
  const date = article.published_date
    ? new Date(article.published_date).toLocaleDateString("es-ES", { day: "numeric", month: "long", year: "numeric" })
    : "";
  return (
    <Card
      as="article"
      data-testid={`article-card-${article._id}`}
      to={`/noticias/${article.slug}`}
      className="flex flex-col h-full"
    >
      <SmartImage
        src={article.image}
        alt=""
        ratio="16 / 9"
        fallback={
          <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-primary/10 to-primary/20">
            <img src="/logos/logo-icono.png" alt="" className="h-16 w-auto opacity-20" loading="lazy" decoding="async" />
          </div>
        }
      />
      <CardBody className="flex flex-col flex-1 p-5">
        <div className="flex items-center gap-2 mb-3 flex-wrap">
          <span className="inline-flex items-center gap-1 text-xs font-semibold text-primary bg-primary/10 px-2.5 py-1 rounded-full">
            <Tag className="w-3 h-3" aria-hidden="true" />
            {CAT_LABELS[article.category] || article.category}
          </span>
          {date && (
            <span className="flex items-center gap-1 text-xs text-muted-foreground">
              <Calendar className="w-3 h-3" aria-hidden="true" />{date}
            </span>
          )}
        </div>
        <h2 className="font-heading font-bold text-base leading-snug mb-2 group-hover:text-primary transition-colors duration-fast line-clamp-2">
          {article.title}
        </h2>
        {article.summary && (
          <p className="text-sm text-muted-foreground leading-relaxed mb-4 flex-1 line-clamp-3">
            {article.summary.length > 150 ? article.summary.slice(0, 150) + "…" : article.summary}
          </p>
        )}
        <span
          data-testid={`read-more-${article._id}`}
          className="mt-auto inline-flex items-center gap-1.5 text-sm font-semibold text-primary group-hover:gap-2.5 transition-[gap] duration-fast"
        >
          Leer más <ChevronRight className="w-4 h-4" aria-hidden="true" />
        </span>
      </CardBody>
    </Card>
  );
}

export default function Noticias() {
  const [articles, setArticles] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [page, setPage] = useState(1);
  const [category, setCategory] = useState("all");
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [loading, setLoading] = useState(true);

  // Este listado es paginado y filtrable, así que no pasa por la caché
  // compartida: la clave dependería de página, categoría y búsqueda a la vez.
  const load = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams({ page, per_page: 9 });
    if (category !== "all") params.set("category", category);
    if (search) params.set("search", search);
    apiGet(`/articles?${params}`)
      .then(data => {
        setArticles(data.articles || []);
        setTotal(data.total || 0);
        setTotalPages(data.total_pages || 1);
      })
      .catch(() => setArticles([]))
      .finally(() => setLoading(false));
  }, [page, category, search]);

  useEffect(() => { load(); }, [load]);

  const handleCategory = cat => { setCategory(cat); setPage(1); };
  const handleSearch = e => { e.preventDefault(); setSearch(searchInput); setPage(1); };
  const limpiarFiltros = () => { setSearch(""); setSearchInput(""); setCategory("all"); setPage(1); };

  return (
    <div data-testid="noticias-page" className="min-h-screen">
      <SEO
        title="Noticias y actualidad"
        description="Actualidad de la Delegación de Evangelización Digital, de la Diócesis de Huelva y de la Iglesia en España."
      />
      <PageHero slug="noticias" />

      <Section tone="light" space="tight">
        {/* Buscador */}
        <form onSubmit={handleSearch} className="mb-8 flex gap-3 max-w-xl mx-auto" role="search">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" aria-hidden="true" />
            <label htmlFor="buscar-noticias" className="sr-only">Buscar noticias</label>
            <input
              id="buscar-noticias"
              data-testid="noticias-search"
              type="search"
              value={searchInput}
              onChange={e => setSearchInput(e.target.value)}
              placeholder="Buscar noticias..."
              className="w-full pl-10 pr-4 py-2.5 rounded-full border border-input bg-card text-sm focus-ring"
            />
          </div>
          <CTAButton type="submit" data-testid="noticias-search-btn" className="font-semibold">
            Buscar
          </CTAButton>
        </form>

        {/* Filtros de categoría */}
        <div className="flex justify-center mb-10">
          <FilterPills
            options={CATEGORIES}
            value={category}
            onChange={handleCategory}
            label="Filtrar noticias por categoría"
            className="justify-center"
            data-testid="category-filters"
          />
        </div>

        {/* Resultados. La línea de recuento se reserva siempre para que la
            rejilla no se desplace al terminar la carga. */}
        <p className="text-sm text-muted-foreground mb-6 min-h-[1.25rem]" aria-live="polite">
          {loading ? "" : `${total} artículo${total !== 1 ? "s" : ""} encontrado${total !== 1 ? "s" : ""}`}
        </p>

        {loading ? (
          <CardGridSkeleton count={6} />
        ) : articles.length === 0 ? (
          <EmptyState
            icon={Newspaper}
            title="No se encontraron noticias"
            description="Prueba con otros términos o quita los filtros aplicados."
            action={
              (search || category !== "all") && (
                <CTAButton onClick={limpiarFiltros} variant="outline">Limpiar filtros</CTAButton>
              )
            }
          />
        ) : (
          <>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
              {articles.map((a, i) => (
                <Reveal key={a._id} delay={Math.min(i, 5) * 70} distance={24}>
                  <ArticleCard article={a} />
                </Reveal>
              ))}
            </div>

            {/* Paginación */}
            {totalPages > 1 && (
              <nav className="flex items-center justify-center gap-3" data-testid="pagination" aria-label="Paginación de noticias">
                <CTAButton
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                  variant="outline"
                  data-testid="prev-page"
                  className="font-semibold"
                >
                  <ChevronLeft className="w-4 h-4" aria-hidden="true" /> Anterior
                </CTAButton>
                <span className="text-sm text-muted-foreground" aria-current="page">
                  Página {page} de {totalPages}
                </span>
                <CTAButton
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  variant="outline"
                  data-testid="next-page"
                  className="font-semibold"
                >
                  Siguiente <ChevronRight className="w-4 h-4" aria-hidden="true" />
                </CTAButton>
              </nav>
            )}
          </>
        )}
      </Section>

      <FloatingEditButton slug="noticias" />
    </div>
  );
}
